/**
 * ConflictWarningModal — shown when a proposed schedule overlaps an
 * existing session. Lists each conflict; admin can Cancel (abort) or
 * Override (proceed anyway, ``override_conflicts: true``).
 */

import React from 'react';

import { Button, Modal } from '@/components/common';
import type { ConflictItem } from '@features/calendar/api/conflicts';

interface Props {
  isOpen: boolean;
  onClose: () => void;
  onOverride: () => void;
  conflicts: ConflictItem[];
  busy?: boolean;
}

function formatLocal(iso: string): string {
  return new Date(iso).toLocaleString('en-US', {
    weekday: 'short',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
  });
}

export const ConflictWarningModal: React.FC<Props> = ({
  isOpen,
  onClose,
  onOverride,
  conflicts,
  busy = false,
}) => {
  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title="Schedule conflict"
      size="md"
      data-testid="candeza-calendar-conflict-modal"
    >
      <p
        data-testid="candeza-calendar-conflict-description"
        style={{ marginTop: 0, color: '#475569', fontSize: 14 }}
      >
        The proposed time overlaps {conflicts.length} existing session
        {conflicts.length === 1 ? '' : 's'}. Review below and decide whether
        to proceed.
      </p>

      <ul
        data-testid="candeza-calendar-conflict-list"
        style={{
          listStyle: 'none',
          padding: 0,
          margin: '12px 0',
          borderTop: '1px solid #e2e8f0',
        }}
      >
        {conflicts.map((c) => (
          <li
            key={c.session_id}
            data-testid={`candeza-calendar-conflict-row-${c.session_id}`}
            data-conflict-type={c.conflict_type}
            style={{
              padding: '10px 0',
              borderBottom: '1px solid #e2e8f0',
              fontSize: 13,
            }}
          >
            <div style={{ fontWeight: 600 }}>
              {c.conflict_type === 'teacher' ? '👤 Teacher' : '🚪 Room'} overlap
            </div>
            <div style={{ color: '#64748b' }}>
              {formatLocal(c.starts_at)} → {formatLocal(c.ends_at)}
            </div>
          </li>
        ))}
      </ul>

      <div
        style={{
          display: 'flex',
          gap: 8,
          justifyContent: 'flex-end',
          marginTop: 16,
        }}
      >
        <Button
          variant="ghost"
          onClick={onClose}
          data-testid="candeza-calendar-conflict-cancel-btn"
        >
          Back
        </Button>
        <Button
          variant="danger"
          onClick={onOverride}
          disabled={busy}
          data-testid="candeza-calendar-conflict-override-btn"
        >
          {busy ? 'Saving…' : 'Override and proceed'}
        </Button>
      </div>
    </Modal>
  );
};

